import React, { useState, useMemo } from "react";
import {
  Box,
  Chip,
  Paper,
  Typography,
  Autocomplete,
  TextField,
  InputAdornment,
} from "@mui/material";
import { Search as SearchIcon, Clear as ClearIcon } from "@mui/icons-material";

export interface ChipSelectorOption<T = any> {
  value: T;
  label: string;
  subtitle?: string;
  color?: string;
}

interface ChipSelectorProps<T = any> {
  options: ChipSelectorOption<T>[];
  selectedValues: T[];
  onChange: (newSelectedValues: T[]) => void;
  placeholder?: string;
  label?: string;
  maxHeight?: number;
  variant?: 'outlined' | 'filled';
  size?: 'small' | 'medium';
  showSubtitle?: boolean;
  customChipColor?: (value: T) => string;
  noOptionsText?: string;
}

const ChipSelector = <T = any>({
  options,
  selectedValues,
  onChange,
  placeholder = "חפש והוסף פריט...",
  label,
  maxHeight = 300,
  variant = 'outlined',
  size = 'small',
  showSubtitle = true,
  customChipColor,
  noOptionsText = "לא נמצאו תוצאות",
}: ChipSelectorProps<T>) => {
  const [searchValue, setSearchValue] = useState<string>("");

  // פונקציה לחישוב ציון התאמה של אופציה לפי מילות החיפוש
  const calculateMatchScore = (option: ChipSelectorOption<T>, searchWords: string[]): number => {
    const textToSearch = `${option.label} ${option.subtitle || ''}`.toLowerCase();
    let score = 0;

    searchWords.forEach(word => {
      if (textToSearch.includes(word.toLowerCase())) {
        score += 1;
      }
    });

    return score;
  };

  // פילטר אופציות שלא נבחרו עדיין עם סינון ומיון לפי מילות חיפוש
  const availableOptions = useMemo(() => {
    const unselectedOptions = options.filter(option => !selectedValues.includes(option.value));

    if (!searchValue.trim()) {
      return unselectedOptions;
    }

    // פיצול לפי רווחים למילים נפרדות
    const searchWords = searchValue.trim().split(/\s+/).filter(word => word.length > 0);

    if (searchWords.length === 0) {
      return unselectedOptions;
    }

    // סינון ומיון לפי ציון התאמה
    const scoredOptions = unselectedOptions
      .map(option => ({
        option,
        score: calculateMatchScore(option, searchWords)
      }))
      .filter(item => item.score > 0)
      .sort((a, b) => b.score - a.score);

    return scoredOptions.map(item => item.option);
  }, [options, selectedValues, searchValue]);

  // מציאת אופציות שנבחרו לתצוגת chips
  const selectedOptions = useMemo(
    () => options.filter(option => selectedValues.includes(option.value)),
    [options, selectedValues]
  );

  const handleAddItem = (option: ChipSelectorOption<T> | null) => {
    if (option && !selectedValues.includes(option.value)) {
      onChange([...selectedValues, option.value]);
      setSearchValue("");
    }
  };

  const handleRemoveItem = (valueToRemove: T) => {
    onChange(selectedValues.filter(value => value !== valueToRemove));
  };

  const getChipColor = (option: ChipSelectorOption<T>) => {
    if (customChipColor) {
      return customChipColor(option.value);
    }
    return option.color || 'primary';
  };

  // פונקציה להדגשת מילות חיפוש בטקסט
  const highlightSearchWords = (text: string, searchWords: string[]) => {
    if (!searchWords.length) return text;

    let result = text;
    const parts: { text: string; isBold: boolean }[] = [];
    let currentIndex = 0;

    // יצירת רגקס לכל מילות החיפוש
    const pattern = searchWords
      .map(word => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')) // escape special chars
      .join('|');
    const regex = new RegExp(`(${pattern})`, 'gi');

    const matches = Array.from(text.matchAll(regex));

    matches.forEach(match => {
      const matchIndex = match.index!;
      // הוסף טקסט רגיל לפני ההתאמה
      if (matchIndex > currentIndex) {
        parts.push({ text: text.substring(currentIndex, matchIndex), isBold: false });
      }
      // הוסף טקסט מודגש
      parts.push({ text: match[0], isBold: true });
      currentIndex = matchIndex + match[0].length;
    });

    // הוסף את שאר הטקסט
    if (currentIndex < text.length) {
      parts.push({ text: text.substring(currentIndex), isBold: false });
    }

    return parts.length > 0 ? parts : [{ text, isBold: false }];
  };

  return (
    <Paper 
      elevation={variant === 'outlined' ? 0 : 1} 
      {...(variant === 'outlined' ? { variant: 'outlined' } : {})}
      sx={{ p: 2, bgcolor: variant === 'filled' ? 'grey.50' : 'transparent' }}
    >
      {label && (
        <Typography variant="subtitle2" gutterBottom>
          {label}
        </Typography>
      )}

      {/* אזור החיפוש */}
      <Box sx={{ mb: selectedOptions.length > 0 ? 2 : 0 }}>
        <Autocomplete
          options={availableOptions}
          value={null}
          inputValue={searchValue}
          onInputChange={(_, newValue) => setSearchValue(newValue)}
          onChange={(_, newValue) => handleAddItem(newValue)}
          getOptionLabel={(option) => option.label}
          filterOptions={(x) => x} // השתמש בפילטר שלנו במקום הדיפולטי
          noOptionsText={noOptionsText}
          size={size}
          sx={{ maxWidth: 400 }}
          disablePortal={false}
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: "preventOverflow",
                  options: {
                    boundary: "viewport",
                  },
                },
              ],
            },
            listbox: {
              sx: {
                maxHeight: maxHeight,
                direction: "rtl",
                textAlign: "right",
                paddingRight: 1,
              },
            },
          }}
          renderOption={(props, option) => {
            const searchWords = searchValue.trim().split(/\s+/).filter(word => word.length > 0);
            const labelParts = highlightSearchWords(option.label, searchWords);
            const subtitleParts = option.subtitle ? highlightSearchWords(option.subtitle, searchWords) : null;

            return (
              <li {...props} key={`${option.value}-${option.label}`}>
                <Box>
                  <Typography variant="body2">
                    {typeof labelParts === 'string' ? (
                      labelParts
                    ) : (
                      labelParts.map((part, index) => (
                        part.isBold ? (
                          <Box component="span" key={index} sx={{ fontWeight: 'bold' }}>
                            {part.text}
                          </Box>
                        ) : (
                          <Box component="span" key={index}>{part.text}</Box>
                        )
                      ))
                    )}
                  </Typography>
                  {showSubtitle && option.subtitle && (
                    <Typography variant="caption" color="text.secondary">
                      {typeof subtitleParts === 'string' ? (
                        subtitleParts
                      ) : subtitleParts ? (
                        subtitleParts.map((part, index) => (
                          part.isBold ? (
                            <Box component="span" key={index} sx={{ fontWeight: 'bold' }}>
                              {part.text}
                            </Box>
                          ) : (
                            <Box component="span" key={index}>{part.text}</Box>
                          )
                        ))
                      ) : null}
                    </Typography>
                  )}
                </Box>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={placeholder}
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon sx={{ color: "gray" }} />
                  </InputAdornment>
                ),
                startAdornment: searchValue ? (
                  <InputAdornment position="start">
                    <ClearIcon 
                      sx={{ cursor: 'pointer', color: 'gray' }}
                      onClick={() => setSearchValue("")}
                    />
                  </InputAdornment>
                ) : null,
              }}
            />
          )}
        />
      </Box>

      {/* הצגת הפריטים שנבחרו כ-chips */}
      {selectedOptions.length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            פריטים שנבחרו:
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {selectedOptions.map((option) => (
              <Chip
                key={`selected-${option.value}-${option.label}`}
                label={option.label}
                onDelete={() => handleRemoveItem(option.value)}
                sx={{
                  backgroundColor: customChipColor ? customChipColor(option.value) : undefined,
                  color: customChipColor ? 'white' : undefined,
                  '& .MuiChip-deleteIcon': {
                    color: customChipColor ? 'rgba(255,255,255,0.7)' : undefined,
                    '&:hover': {
                      color: customChipColor ? 'white' : undefined,
                    }
                  }
                }}
                color={customChipColor ? undefined : (getChipColor(option) as any)}
                variant="filled"
                size={size}
              />
            ))}
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default ChipSelector;